import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import type { VisibleCellsChangedOutput } from '../../hooks/useVisibleCellsChanged.types';
import type { MinimalSubscriptionResponse } from '../../types/SubscriptionResponse';
import { getAgGridColId } from './columns';
import type { IEnrichableEntity } from './IEnrichableEntity';

interface UseBlotterVisibleRowsEnrichmentParams<Entity extends IEnrichableEntity<Entity, Enrichment>, Enrichment> {
  /** The basic dataObservable which you just normally would pass into useBlotterTable for example */
  dataObservable: Observable<MinimalSubscriptionResponse<Entity>>;
  /**
   * An observable representing the enrichment data. It should be returning the basic latest json (SubscriptionResponse)
   * message, as well as a "cache" property which represents all latest data, including the latest json which is represented in
   * the same message. The cache is necessary to support use cases where multiple rows can be sharing the same enrichment data.
   */
  enrichmentObservable: Observable<{ json: MinimalSubscriptionResponse<Enrichment>; cache: Map<string, Enrichment> }>;
  /** The output from useVisibleCellsChanged hook, which should be hooked up to the blotter in question */
  visibleCellsChangedObs: Observable<VisibleCellsChangedOutput<Entity>>;
  /** Get the unique key of the item (row) in question, essentially just the rowID of the item */
  getUniqueKey: (item: Entity) => string;
  /** Get the unique key so we can resolve the enrichment data type properly */
  getUniqueEnrichmentKey: (item: Enrichment) => string;
  /** Resolve an enrichment lookup key for a given visible column. */
  getEnrichmentLookupKey: (args: {
    item: Entity;
    column: VisibleCellsChangedOutput<Entity>['visibleColumns'][number];
  }) => string | undefined;
}

/**
 * This hook provides you with the ability to enrich the visible rows of the blotter with additional properties.
 *
 * The hook needs to be used in tandem with the useVisibleCellsChanged hook. The necessary `enrichmentObservable` also
 * should rely on the useVisibleCellsChanged hook. The logic within the provided enrichmentObservable, as in what backend subscriptions it creates,
 * should reasonably be based on the same set of current visible rows and columns as this hook is. In fact, that is the assumption.
 */
export const useBlotterVisibleRowsEnrichment = <Entity extends IEnrichableEntity<Entity, Enrichment>, Enrichment>({
  dataObservable,
  enrichmentObservable,
  visibleCellsChangedObs,
  getUniqueKey,
  getUniqueEnrichmentKey,
  getEnrichmentLookupKey,
}: UseBlotterVisibleRowsEnrichmentParams<Entity, Enrichment>) => {
  const outputObs: Observable<MinimalSubscriptionResponse<Entity>> = useMemo(
    () =>
      new Observable(output => {
        let renderedItems: Entity[] = [];
        let renderedItemsMap: Map<string, Entity> = new Map();
        let enrichmentCache = new Map<string, Enrichment>();
        let currentVisibleColumns: VisibleCellsChangedOutput<Entity>['visibleColumns'] = [];

        /**
         * The updateRenderedItems function is a helper function which overrides existing renderedItems with their new revision
         * @param changedItems the items which have changed
         */
        function updateRenderedItems(changedItems: Entity[]) {
          const changedItemsMap = new Map(changedItems.map(item => [getUniqueKey(item), item]));
          renderedItems = renderedItems.map(item => {
            const changedItem = changedItemsMap.get(getUniqueKey(item));
            return changedItem ?? item;
          });
          renderedItemsMap = new Map(renderedItems.map(item => [getUniqueKey(item), item]));
        }

        const dataSub = dataObservable.subscribe(json => {
          /**
           * We received new base data. There are a few situations we need to handle:
           * 1. Initial data. If the data received is initial, just pass it along as is.
           * We do not attempt to apply any enrichments in this case. We are receiving an initial message, and for our purposes
           * it is treated as a kind of reset message. Because it is a kind of reset message, we assume that new enrichments will follow later.
           *
           * 2. A delta update. If we receive a delta update, we need to check if we are in the set of renderedItems.
           * If we are in the set of renderedItems, then we might have to attach existing enrichments onto ourselves.
           */

          if (json.initial) {
            renderedItems = [];
            output.next(json);
          } else {
            const outputItems: Entity[] = [];
            for (const item of json.data) {
              const renderedItem = renderedItemsMap.get(getUniqueKey(item));
              if (renderedItem != null) {
                // The item we received a delta update for is one of the rendered nodes, and thus might have extra enrichments attached.
                // Move the attached enrichment from the old revision of the record to the new one, and then add the new record
                // to the output array
                item.addEnrichments(renderedItem.getEnrichments());
                outputItems.push(item);
              } else {
                // Otherwise the node we received an update for is not rendered, and thus cannot have any enrichment attached.
                // We simply forward the updated item without tampering with it in any way.
                outputItems.push(item);
              }
            }

            updateRenderedItems(outputItems);
            output.next({
              ...json,
              data: outputItems,
            });
          }
        });

        const visibleCellsChangedSub = visibleCellsChangedObs.subscribe(
          ({
            renderedItems: newRenderedItems,
            renderedItemsMap: newRenderedItemsMap,
            removedItems,
            visibleColumns,
            removedColumns,
            addedColumns,
          }) => {
            /**
             * We keep a reference to these data sets in this pipe too as they are useful for when we receive
             * updates from the other streams, so assign these right away so we're up to date.
             */
            renderedItems = newRenderedItems;
            renderedItemsMap = newRenderedItemsMap;
            currentVisibleColumns = visibleColumns;

            /**
             * The visible cells have changed. This can be a result of either rows or columns going out of / into view.
             * While a user can only do one of the possible four actions: hide row, show row, hide column, show column, we need
             * to keep in mind that programmatically you could do all four at once. This code is written with this in mind.
             *
             * We need to do a few things:
             *
             * 1. Remove the no-longer-visible enrichment from the RowNodes that are no longer visible.
             *
             * 2. Go through all present renderedItems and check that all relevant enrichments are applied. We iterate over all renderedItems, as opposed to just the new addedItems,
             * because there is a case where you could filter down to some subset of the previously renderedItems through an initial: true message. For example, if you were to
             * right-click filter on some Security.Symbol in the Price Grid, that'd cause an initial: true message to come across (without any enrichment). There is no way for us to
             * attach this enrichment back again from the cache without on every change of visible cells asserting that all necessary enrichments are applied.
             *
             * 3. A column might have been hidden. If a column has been hidden, we want to remove any enriched data related to that now hidden column from all records.
             * For each removed column, we go through all rendered items and ensure that the field which that column is connected to is _not_ populated. If it is populated,
             * remove that enrichment by sending a new cleaned revision of that item.
             */

            // Step 1 - handle removed items
            if (removedItems.length > 0) {
              const cleanedRemovedItems = removedItems.map(item => {
                const clone = item.clone();
                clone.clearEnrichments();
                return clone;
              });

              // We don't need to call updateRenderedItems(cleanedRemovedItems) here because these items
              // have already been effectively removed by doing renderedItems = newRenderedItems above.

              output.next({
                initial: false,
                ts: '',
                data: cleanedRemovedItems,
              });
            }

            // Step 2 - assert that all enrichments are applied on our renderedItems
            if (renderedItems.length > 0) {
              const outputItems: Entity[] = [];

              // We just want to check the visible columns that werent added in _this_ message.
              // the added columns added in this message will be checked by step 3 below.
              const addedEnrichmentColumnIDs = new Set(addedColumns.map(column => getAgGridColId(column.getColDef())));
              const enrichmentColumnsToCheck = visibleColumns.filter(
                col => !addedEnrichmentColumnIDs.has(getAgGridColId(col.getColDef()))
              );

              for (const renderedItem of renderedItems) {
                let changed = false;
                const workingItem = renderedItem.clone();
                for (const column of enrichmentColumnsToCheck) {
                  const lookupKey = getEnrichmentLookupKey({ item: workingItem, column });
                  if (!lookupKey) {
                    continue;
                  }

                  const cachedEnrichment = enrichmentCache.get(lookupKey);
                  if (!workingItem.hasEnrichment(lookupKey) && cachedEnrichment) {
                    changed = true;
                    workingItem.addEnrichments([cachedEnrichment]);
                  }
                }

                if (changed) {
                  outputItems.push(workingItem);
                }
              }

              if (outputItems.length > 0) {
                updateRenderedItems(outputItems);
                output.next({
                  initial: false,
                  ts: '',
                  data: outputItems,
                });
              }
            }

            // Step 3 - handle hidden or newly visible columns.
            // We chose to combine hiding and adding into one step so that we could for example add an enrichment and remove an enrichment to one row in a single update.
            if (removedColumns.length > 0 || addedColumns.length > 0) {
              const outputItems: Entity[] = [];

              // For each rendered item, we validate its enrichment so that we populate any new fields (columns becoming visible) with data immediately from the cache,
              // And for columns removed (removedEnrichmentFields), we ensure that we dont leave any potentially stale enrichment data behind on the items
              for (const item of renderedItems) {
                let changed = false;
                const workingItem = item.clone();
                const currentlyVisibleLookupKeys = new Set(
                  compact(visibleColumns.map(column => getEnrichmentLookupKey({ item: workingItem, column })))
                );

                for (const column of addedColumns) {
                  const lookupKey = getEnrichmentLookupKey({ item, column });
                  if (!lookupKey) {
                    continue;
                  }

                  // Ensure that we have populated the enriched data field, or at least try to from the cache
                  const cachedEnrichment = enrichmentCache.get(lookupKey);
                  if (!workingItem.hasEnrichment(lookupKey) && cachedEnrichment) {
                    changed = true;
                    workingItem.addEnrichments([cachedEnrichment]);
                  }
                }

                for (const column of removedColumns) {
                  const lookupKey = getEnrichmentLookupKey({ item: workingItem, column });
                  if (!lookupKey) {
                    continue;
                  }

                  if (workingItem.hasEnrichment(lookupKey) && !currentlyVisibleLookupKeys.has(lookupKey)) {
                    // Remove enrichments that are no longer necessary (no lookup keys are referring to the enrichment anymore)
                    changed = true;
                    workingItem.removeEnrichments([lookupKey]);
                  }
                }

                if (changed) {
                  outputItems.push(workingItem);
                }
              }

              if (outputItems.length > 0) {
                // we send the new update, but we also need to update the renderedItems we have stored since we have changed them
                updateRenderedItems(outputItems);

                output.next({
                  initial: false,
                  ts: '',
                  data: outputItems,
                });
              }
            }
          }
        );

        const enrichmentSub = enrichmentObservable.subscribe(({ json, cache }) => {
          /**
           * We received an enrichment data update.
           *
           * The only real nuance here making this a bit complex is that we support the case where one entity T
           * can require several enrichments E. This means that for each item T, we get the enrichment requirements,
           * defined as an array of keyof E, and for each of the required enrichment keys, apply our enrichments.
           */

          // Update our cache for use when we receive messages from the other streams
          enrichmentCache = cache;

          const receivedEnrichmentDataMap = new Map<string, Enrichment>(
            json.data.map(item => [getUniqueEnrichmentKey(item), item])
          );

          const outputItems: Entity[] = [];
          for (const renderedItem of renderedItems) {
            const wantedEnrichments = compact(
              currentVisibleColumns.map(column =>
                getEnrichmentLookupKey({
                  item: renderedItem,
                  column,
                })
              )
            );
            const applicableReceivedEnrichments = compact(
              wantedEnrichments.map(key => receivedEnrichmentDataMap.get(key))
            );

            if (applicableReceivedEnrichments.length > 0) {
              const workingItem = renderedItem.clone();
              workingItem.addEnrichments(applicableReceivedEnrichments);
              outputItems.push(workingItem);
            }
          }

          if (outputItems.length > 0) {
            // we send the new update, but we also need to update the renderedItems we have stored since we have changed them
            updateRenderedItems(outputItems);

            output.next({
              initial: false,
              ts: '',
              data: outputItems,
            });
          }
        });

        return () => {
          dataSub.unsubscribe();
          visibleCellsChangedSub.unsubscribe();
          enrichmentSub.unsubscribe();
        };
      }),
    [
      dataObservable,
      visibleCellsChangedObs,
      getUniqueEnrichmentKey,
      getUniqueKey,
      enrichmentObservable,
      getEnrichmentLookupKey,
    ]
  );

  return {
    enrichedDataObservable: outputObs,
  };
};
